import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, TrendUp, Briefcase, CurrencyDollar, SignOut, ChartLine } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { pressable } from '../lib/a11y';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InvestorDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [royalties, setRoyalties] = useState([]);
  const [pappaPayouts, setPappaPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/dashboard/investor/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchRoyalties = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/royalties/user/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoyalties(response.data);
    } catch (error) {
      // Non-critical: leave royalties empty if fetch fails
    }
  }, [user]);

  const fetchPappaPayouts = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/pappa/settlements/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPappaPayouts(response.data);
    } catch (error) {
      // optional
    }
  }, [user]);

  // Zingy wallet is parked pending regulatory review.
  // Wallet fetch and hero card were removed from this dashboard; the underlying
  // module lives in /app/frontend/src/pages/ZingyWallet.js and re-enables when
  // ZINGY_ENABLED=true in backend/.env.

  useEffect(() => {
    fetchDashboard();
    fetchRoyalties();
    fetchPappaPayouts();
  }, [fetchDashboard, fetchRoyalties, fetchPappaPayouts]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      
      {/* Navbar */}
      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} data-testid="nav-home-btn" className="flex items-center space-x-3">
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </button>
            <div className="flex items-center space-x-4">
              <button
                data-testid="nav-marketplace-btn"
                onClick={() => navigate('/marketplace')}
                className="btn-secondary"
              >
                Marketplace
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={handleLogout}
                className="btn-secondary flex items-center space-x-2"
              >
                <SignOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-5xl font-bold mb-2">
            INVESTOR <span className="text-gold">DASHBOARD</span>
          </h1>
          <p className="font-body text-xl text-slate-400">Welcome back, {user.name}</p>
        </div>

        {/* Zingy wallet hero card is parked pending regulatory review (Feb 2026).
           Investors will continue to see PAPPA payouts, royalties, and Stripe-based
           investments; virtual-currency redemption/reinvestment will return once the
           module is re-enabled via ZINGY_ENABLED=true. */}

        {/* Stats Grid */}
        <div data-testid="stats-grid" className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <CurrencyDollar size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Total Invested</div>
            <div className="font-mono text-3xl font-bold text-gold tabular-nums">
              ₹{dashboard?.total_invested?.toLocaleString('en-IN') || '0'}
            </div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <TrendUp size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Total Royalties</div>
            <div className="font-mono text-3xl font-bold text-gold tabular-nums">
              ₹{dashboard?.total_royalties?.toLocaleString('en-IN') || '0'}
            </div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <Briefcase size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">Active Investments</div>
            <div className="font-mono text-3xl font-bold tabular-nums">
              {dashboard?.active_investments || 0}
            </div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <ChartLine size={40} className="text-gold mb-4" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">ROI</div>
            <div className="font-mono text-3xl font-bold text-gold tabular-nums">
              {dashboard?.total_invested > 0
                ? `${((dashboard.total_royalties / dashboard.total_invested) * 100).toFixed(1)}%`
                : '0%'}
            </div>
          </div>
        </div>

        {/* Investments List */}
        <div className="mb-12">
          <h2 className="font-heading text-3xl font-bold mb-6">MY INVESTMENTS</h2>
          {dashboard?.investments?.length === 0 ? (
            <div className="bg-card border border-white/5 rounded-lg p-12 text-center">
              <Briefcase size={64} className="text-slate-400 mx-auto mb-4" />
              <p className="font-body text-xl text-slate-400 mb-6">No investments yet</p>
              <button
                data-testid="browse-movies-btn"
                onClick={() => navigate('/marketplace')}
                className="btn-primary"
              >
                Browse Movies
              </button>
            </div>
          ) : (
            <div data-testid="investments-list" className="space-y-4">
              {dashboard.investments.map((investment) => (
                <motion.div
                  key={investment.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors cursor-pointer"
                  {...pressable(() => navigate(`/movie/${investment.movie_id}`), `Open investment in ${investment.movie_title}`)}
                  data-testid={`investment-${investment.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-heading text-xl font-bold">{investment.movie_title}</h3>
                        {investment.escrow_status === 'holding' && (
                          <span className="px-2 py-0.5 bg-gold/10 border border-gold/30 rounded-sm text-gold text-[10px] font-bold uppercase tracking-wide">
                            🔒 In Escrow
                          </span>
                        )}
                        {investment.escrow_status === 'released' && (
                          <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                            ✓ Released
                          </span>
                        )}
                        {investment.escrow_status === 'refunded' && (
                          <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-[10px] font-bold uppercase tracking-wide">
                            ↩ Refunded
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm flex-wrap">
                        <span className="font-body text-slate-400">
                          Tier: <span className="text-white font-medium">{investment.tier_name}</span>
                        </span>
                        <span className="font-body text-slate-400">
                          Royalty: <span className="text-gold font-bold">{investment.royalty_percentage}%</span>
                        </span>
                        <span className="font-body text-slate-400">
                          Date: {new Date(investment.investment_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-body text-xs text-slate-400 mb-1">Invested</div>
                      <div className="font-mono text-2xl font-bold text-gold tabular-nums">
                        ₹{investment.amount.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* PAPPA Settlements */}
        {pappaPayouts.length > 0 && (
          <div className="mb-12" data-testid="pappa-payouts-section">
            <h2 className="font-heading text-3xl font-bold mb-6">PAPPA <span className="text-gold">PAYOUTS</span></h2>
            <div className="space-y-4">
              {pappaPayouts.map((p, idx) => (
                <div key={idx} className="bg-card border border-amber-500/30 rounded-lg p-6" data-testid={`pappa-payout-${idx}`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-heading text-xl font-bold">{p.movie_title}</h3>
                        <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm text-amber-300 text-[10px] font-bold uppercase tracking-wide">PAPPA · Settled</span>
                      </div>
                      <div className="font-body text-xs text-slate-400">Settled on {new Date(p.settled_at).toLocaleDateString()} · Markup {p.markup_pct}%</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                        <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                          <div className="text-[10px] uppercase text-slate-400">Principal returned</div>
                          <div className="font-mono text-base font-bold tabular-nums">₹{p.principal_inr.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                          <div className="text-[10px] uppercase text-slate-400">Markup earned</div>
                          <div className="font-mono text-base font-bold tabular-nums text-emerald-400">+₹{p.markup_inr.toLocaleString('en-IN')}</div>
                        </div>
                        <div className="bg-white/[0.03] border border-white/5 rounded p-3">
                          <div className="text-[10px] uppercase text-slate-400">You received</div>
                          <div className="font-mono text-base font-bold tabular-nums text-gold">₹{p.total_payout_inr.toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Royalties History */}
        <div>
          <h2 className="font-heading text-3xl font-bold mb-6">ROYALTY HISTORY</h2>
          {royalties.length === 0 ? (
            <div className="bg-card border border-white/5 rounded-lg p-12 text-center">
              <TrendUp size={64} className="text-slate-400 mx-auto mb-4" />
              <p className="font-body text-xl text-slate-400">No royalty payments yet</p>
            </div>
          ) : (
            <div data-testid="royalties-list" className="space-y-4">
              {royalties.map((distribution) => (
                <div
                  key={distribution.id}
                  className="bg-card border border-white/5 rounded-lg p-6"
                  data-testid={`royalty-${distribution.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-heading text-xl font-bold mb-2">{distribution.movie_title}</h3>
                      <div className="font-body text-sm text-slate-400">
                        {new Date(distribution.distribution_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-body text-xs text-slate-400 mb-1">Total Revenue</div>
                      <div className="font-mono text-xl font-bold tabular-nums">
                        ₹{distribution.total_revenue.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {distribution.payments.map((payment) => (
                      <div key={payment.investment_id} className="flex justify-between items-center py-2 border-t border-white/5">
                        <span className="font-body text-sm text-slate-400">Your Royalty Payment</span>
                        <span className="font-mono text-lg font-bold text-gold tabular-nums">
                          ₹{payment.amount.toLocaleString('en-IN')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestorDashboard;